// src/components/ErrorBoundary.tsx
// ★ Catch rendering errors and show a friendly fallback instead of a white screen.
// Wraps children in a React error boundary.

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
    /** Optional custom fallback UI */
    fallback?: ReactNode;
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <h2 style={styles.heading}>Something went wrong</h2>
                        <p style={styles.text}>
                            We're sorry — this page encountered an unexpected error.
                        </p>
                        <button
                            style={styles.button}
                            onClick={() => {
                                this.setState({ hasError: false, error: null });
                                window.location.reload();
                            }}
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        padding: 20,
    },
    card: {
        textAlign: 'center' as const,
        maxWidth: 500,
        padding: '40px 30px',
        borderRadius: 16,
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
    },
    heading: {
        fontSize: 22,
        fontWeight: 600,
        marginBottom: 12,
        color: '#212529',
    },
    text: {
        fontSize: 15,
        color: '#6c757d',
        marginBottom: 20,
    },
    button: {
        padding: '10px 24px',
        borderRadius: 8,
        border: '1px solid #212529',
        background: '#212529',
        color: '#fff',
        fontSize: 14,
        cursor: 'pointer',
    },
};

export default ErrorBoundary;
